<template>
  <div
    :class="{
      'sb-multiselect-inner': true,
      'sb-multiselect-inner--error': error,
      'sb-multiselect-inner--disabled': isDisabled,
    }"
    v-bind="$attrs"
    @keydown="handleKeyDown"
  >
    <template v-if="leftIcon">
      <SbIcon
        :name="leftIcon"
        class="sb-multiselect-inner__left-icon"
        :data-testid="`${dataTestid}-left-icon`"
        :color="leftIconColor"
        :style="{ color: leftIconColor }"
      />
    </template>
    <div
      class="sb-multiselect-inner__wrapper"
      :data-placeholder="computedPlaceholder"
    >
      <SelectInnerTag
        v-for="(tagLabel, key) in tagLabels"
        :key="key"
        v-tooltip="tagLabel"
        :item="tagLabel"
        :item-label="itemLabel"
        :item-value="itemValue"
        :show-tag-avatars="useAvatars"
        :is-disabled="isDisabled"
        @keydown="handleTagKeydown($event, tagLabel)"
        @close.prevent="removeItem($event, tagLabel)"
        @click="handleClickTag($event, tagLabel)"
      />
      <input
        v-if="!disableFilter"
        :id="inputId"
        ref="search"
        v-model="searchInputText"
        class="sb-multiselect-inner__input"
        :aria-disabled="isDisabled || isLoading"
        :aria-describedby="
          helperText ? `sb-combobox-${uid}-helper-text` : undefined
        "
        :readonly="isDisabled || isLoading || disableFilter"
        :placeholder="computedPlaceholder"
        :data-testid="`${dataTestid}-search-input`"
        role="combobox"
        :aria-expanded="isOpen"
        type="text"
        :popovertarget="'sb-combobox-options-' + uid"
        popovertargetaction="toggle"
        :aria-labelledby="'sb-combobox-button-' + uid"
        :aria-controls="'sb-combobox-options-' + uid"
        @input="handleInput"
        @focus="handleEmitSearchInput"
      />
    </div>

    <div class="sb-multiselect-inner__right-icons">
      <SbIcon
        v-if="isLoading || error || warning"
        :id="'sb-combobox-status-icon-' + uid"
        :class="{
          'sb-multiselect-inner__status-icon--loading': isLoading,
          'sb-multiselect-inner__status-icon--error': error,
          'sb-multiselect-inner__status-icon--warning': warning,
        }"
        :name="
          isLoading ? 'loading' : error ? 'octagon-alert' : 'triangle-alert'
        "
        :data-testid="`${dataTestid}-${error ? 'error' : 'warning'}-icon`"
      />
      <SbIconButton
        v-else-if="clearable && !isDisabled && tagLabels.length"
        ref="clearButton"
        icon="x-circle"
        label="Clear Selection"
        :data-testid="`${dataTestid}-clear-button`"
        size="small"
        @click="clearSelection"
      />
      <SbIconButton
        :id="'sb-combobox-button-' + uid"
        label="Open option list"
        icon="chevron-down"
        size="small"
        type="button"
        class="sb-select-inner__chevron-dropdown"
        :aria-controls="'sb-combobox-options-' + uid"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        tabindex="-1"
        hide-tooltip
        :data-testid="`${dataTestid}-right-icon`"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { isArray } from '../../../utils'
import SbIcon from '../../Icon'

import { Tooltip } from '../../../directives'
import SelectInnerTag from './SelectInnerTag.vue'
import { hasValidSearchTextLabel, useSelectContext } from '../../Select/helpers'

import SbIconButton from '../../IconButton'
import { type PropType } from 'vue'
import type { MultiSelectValue, Option } from '../types'
import type { IconColor } from '../../Icon/types'

export default {
  name: 'SbSelectInner',

  directives: {
    tooltip: Tooltip,
  },

  components: {
    SbIcon,
    SelectInnerTag,
    SbIconButton,
  },

  props: {
    clearable: Boolean,

    placeholder: {
      type: String,
      default: '',
    },

    inputId: {
      type: String,
      default: '',
    },

    uid: {
      type: String,
      default: '',
    },

    leftIcon: {
      type: String,
      default: null,
    },

    leftIconColor: {
      type: String as PropType<IconColor>,
      default: 'primary',
    },

    allowCreate: Boolean,

    error: Boolean,
    warning: Boolean,
    isLoading: Boolean,
    loadingLabel: {
      type: String,
      default: 'Loading...',
    },

    modelValue: {
      type: Array as PropType<MultiSelectValue[]>,
      default: null,
    },
    options: {
      type: Array as PropType<Option[]>,
      default: () => [],
    },
    itemLabel: {
      type: String,
      default: 'label',
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    searchInput: {
      type: String,
      default: null,
    },

    emitOption: Boolean,
    isOpen: Boolean,
    useAvatars: Boolean,
    isDisabled: Boolean,
    showCaption: Boolean,
    itemCaption: {
      type: String,
      default: 'path',
    },
    disableFilter: Boolean,
    helperText: {
      type: String,
      default: undefined,
    },
  },

  emits: [
    'clear-all-values',
    'show-list',
    'close-list',
    'emit-value',
    'search',
    'keydown-enter',
    'remove-item-value',
    'show-all-options',
    'focus-at-index',
  ],

  setup() {
    const selectContext = useSelectContext()

    return {
      selectContext,
    }
  },

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-multiselect-inner'
    },

    searchInputText: {
      get() {
        if (
          hasValidSearchTextLabel(
            this.searchInput?.toString(),
            this.currentOptionLabel,
          ) &&
          (!this.useAvatars || this.isDisabled)
        ) {
          return this.currentOptionLabel.toString()
        }

        return this.searchInput
      },
      set(value: string) {
        const shouldShowAllOptions = value === this.currentOptionLabel || !value
        this.$emit('show-all-options', shouldShowAllOptions)

        if (value && !value.length && !this.useAvatars) {
          this.$emit('search', undefined)
        } else {
          if (this.isAvatarVisible) {
            this.$emit('clear-all-values')
          }
          this.$emit('search', value)
        }

        return value
      },
    },

    hasValue() {
      if (isArray(this.modelValue)) {
        return this.modelValue?.length > 0
      }

      return this.modelValue !== null
    },

    hasDefaultSlot() {
      const slot = this.$slots?.default && this.$slots?.default()
      const slotIsInner = slot && slot[0]?.key === '_innerSelect'
      const firstSlot = slot && slot[0]
      const slotInnerHasChildren =
        slotIsInner &&
        Array.isArray(firstSlot?.children) &&
        firstSlot.children.length > 0
      return (
        slotInnerHasChildren ||
        (!slotIsInner && slot?.length && slot?.length > 0)
      )
    },

    computedPlaceholder() {
      if (!this.hasValue || !this.tagLabels.length) {
        if (this.isLoading && this.loadingLabel) {
          return this.loadingLabel
        }

        return this.placeholder
      }

      return undefined
    },

    currentOptionLabel() {
      return this.currentOptionValue && this.currentOptionValue[this.itemLabel]
        ? this.currentOptionValue[this.itemLabel]
        : this.modelValue
    },

    currentOptionValue() {
      if (!this.hasValue) {
        return {}
      }

      return this.options.find(
        (opt) => opt[this.itemValue] === this.modelValue[0],
      )
    },

    isTagsVisible() {
      return this.hasValue && !this.hasDefaultSlot
    },

    tagLabels(): Option[] {
      if (!this.hasValue) {
        return []
      }

      if (Array.isArray(this.modelValue)) {
        return this.modelValue
          .map((value) => {
            if (typeof value === 'object') {
              return value
            }

            const selectedOption = this.options.find(
              (option) => option[this.itemValue] === value,
            )

            const shouldCreate = this.allowCreate && !selectedOption

            if (shouldCreate) {
              return { [this.itemValue]: value }
            }

            return selectedOption
          })
          .filter((option) => option !== null && option !== undefined)
      }

      return []
    },

    isAvatarVisible() {
      return this.hasValue && this.useAvatars && Boolean(this.avatarData)
    },

    isSearchTextVisible() {
      return this.isTagsVisible
    },

    avatarData() {
      return this.options.find((option) => {
        return option[this.itemValue] === this.modelValue[0]
      })
    },

    context() {
      return this.selectContext()
    },

    tagsCountTooltip() {
      return {
        label: this.tagLabels
          ?.map((item) => item[this.itemLabel])
          .slice(1)
          .join(', '),
        position: 'top',
      }
    },
  },

  watch: {
    modelValue(val, oldVal) {
      const valueExists = oldVal?.length && val?.length
      const hasSameValue = JSON.stringify(val) === JSON.stringify(oldVal)
      const canFocusInput =
        this.isSearchTextVisible && valueExists && !hasSameValue

      if (canFocusInput) {
        this.$nextTick(() => this.handleInputFocus())
      }
    },
    currentOptionLabel: {
      handler(label) {
        if (
          label &&
          this.currentOptionValue &&
          !this.useAvatars &&
          !this.isLoading
        ) {
          this.setSearchTextToLabel(label)
        }
      },
    },
  },

  methods: {
    /**
     * handle with input focus
     */
    handleInputFocus() {
      const input = this.$refs.search
      if (input instanceof HTMLInputElement) {
        input.focus()
      }
    },
    /**
     * forward the 'emit-value' event
     * @param {String} value
     */
    handleEmitValue(value: string) {
      this.$emit('emit-value', value)
    },

    /**
     * forward the 'input' event
     */
    handleEmitSearchInput() {
      if (!this.isDisabled) {
        this.$emit('search', this.searchInputText)
      }
    },

    /**
     * clear all items from value
     */
    clearAllValues(event: Event) {
      event.stopPropagation()
      event.preventDefault()
      this.$emit('clear-all-values')
    },

    /**
     * handle with keydown event in the clear all button
     */
    clearAllValuesKeydown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        this.clearAllValues(event)
      }
    },

    /**
     * remove an item from value or name
     */
    removeItem(event: Event, selectedTag: Option) {
      event.stopPropagation()
      event.preventDefault()
      this.$emit('remove-item-value', this.getComputedTagValue(selectedTag))
    },

    /**
     * handles inner keydown element
     */
    handleKeyDown(event: KeyboardEvent) {
      if (this.isDisabled || this.isLoading) return
      event.stopPropagation()
      const { focusOnFirstItem, focusOnLastItem } = this.context

      if (event.key === 'Enter') {
        event.preventDefault()
        if (this.allowCreate && this.searchInputText?.length) {
          this.handleEmitValue(this.searchInputText)
          this.$emit('search', '')
        } else {
          this.$emit('keydown-enter')
        }
      }

      if (event.key === 'Backspace') {
        this.$emit('show-all-options', false)
        const tagsLength = this.tagLabels.length
        if (tagsLength >= 1 && !this.searchInputText?.length) {
          // it would be better if we work with the virtual DOM nodes
          // not the real DOM
          document
            .querySelectorAll<HTMLDivElement>('.sb-tag')
            [tagsLength - 1]?.focus()
        }
      }

      if (event.key === 'Escape') {
        this.$emit('close-list')
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        focusOnFirstItem()
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        focusOnLastItem()
      }
    },

    /**
     * handles with keydown and emits the remove-item-value event
     */
    handleTagKeydown(event: KeyboardEvent, tagValue: Option) {
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'Backspace' ||
        event.key === 'Delete'
      ) {
        this.$emit('remove-item-value', this.getComputedTagValue(tagValue))
      }

      event.stopPropagation()
    },

    /**
     * get the tag value based on emitOption property
     */
    getComputedTagValue(tag: Option) {
      const value = this.emitOption ? tag : tag[this.itemValue]
      return value !== undefined ? value : tag
    },

    setSearchTextToLabel(label: string) {
      this.$emit('search', undefined)
      this.$nextTick(() => {
        if (hasValidSearchTextLabel(this.searchInput?.toString(), label)) {
          this.searchInputText = label
        }
      })
    },

    clearSelection(event: Event) {
      event.stopPropagation()
      event.preventDefault()
      this.$emit('clear-all-values')
    },

    handleClickTag(event: Event, item: Option) {
      if (this.isOpen) {
        event.stopPropagation()
        event.preventDefault()
        this.$emit('focus-at-index', item)
      }
    },

    handleInput() {
      if (this.isDisabled || this.isLoading) return
      this.$emit('show-list', false)
    },
  },
}
</script>
